import type { IComponentController, IOnChangesObject, IHttpService, IAugmentedJQuery } from 'angular';

import template from './mx-geo-map.component.html';
import { AnyObject } from '../../Types';
import type { Layer, LayerGroup, Map } from 'leaflet';
import type * as Leaflet from 'leaflet';
import type { HttpService } from '../../App/services/http-service';
import { utils } from '../../Utils/mx-utils';

export interface Point {
	lat: number;
	lon: number;
	title?: string;
	'Sys-ObjectId'?: string;
	'Sys-Name'?: string;
	'Sys-Entity'?: string;
}

export class GeoMapController implements IComponentController {
	private zoom?: number;
	private sourceData?: AnyObject[];
	private addressAttribute = 'address';
	private latAttribute = 'lat';
	private lngAttribute = 'lon';
	private titleAttribute = 'title';
	public mxAtId: string = `mx-geo-map-${Math.round(Math.random() * 1000000)}`;
	private leafletMap?: Map;
	private defaultZoom = 5;
	private addressCache: Record<string, [number, number] | Promise<Point | null> | null> = JSON.parse(localStorage.getItem('mxGeoMapCache') || '{}');
	private markerGroup?: LayerGroup;
	private singleMarker?: Layer;
	private leaflet?: typeof Leaflet;
	private centeringMapTimeout?: ReturnType<typeof setTimeout>;
	public isLoading = false;
	private loadingScriptsPromise: Promise<void> = this.loadScripts();

	static readonly $inject = ['$http', 'mx.SolutionBuilderAgent.Http', '$element', '$compile', '$rootScope'];

	constructor(
		private readonly $http: IHttpService,
		private readonly http: HttpService,
		private readonly $element: IAugmentedJQuery,
		private readonly $compile: ng.ICompileService,
		private readonly $rootScope: ng.IRootScopeService
	) {}

	async $onInit() {
		await this.initMap([0, 0], 1);
	}

	$postLink(): void {
		this.handleContainerResizing();
	}

	async $onChanges(changes: IOnChangesObject) {
		if (Array.isArray(changes.sourceData?.currentValue) || changes.zoom) {
			this.isLoading = true;
			try {
				const sourceData = this.sourceData || [];

				const addressesWithoutCoords = (sourceData || [])
					.filter((loc: AnyObject) => {
						return (
							(!loc[this.latAttribute] || !loc[this.lngAttribute]) &&
							loc[this.addressAttribute] &&
							typeof this.addressCache[loc[this.addressAttribute].toUpperCase()] === 'undefined'
						);
					})
					.map((loc: AnyObject) => loc[this.addressAttribute].toUpperCase())
					.filter((address: string, index: number, self: string[]) => self.indexOf(address) === index); // filter out duplicates

				if (addressesWithoutCoords.length) {
					await this.loadAddressesFromServerCache(addressesWithoutCoords);
				}

				// Split sourceData into chunks and perform one by one for optimization
				const finalSuccessPointsToDraw: Point[] = [];
				let addressesToBeSavedIntoServerCache: string[] = [];

				const chunkSize = addressesWithoutCoords.some((a: string) => typeof this.addressCache[a] === 'undefined') ? 10 : sourceData.length;

				for (let i = 0; i < sourceData.length; i += chunkSize) {
					const sourceDataChunk = sourceData.slice(i, i + chunkSize);

					const points = await Promise.allSettled(
						(sourceDataChunk || []).map(async (loc: AnyObject) => {
							const title = loc[this.titleAttribute] || loc[this.addressAttribute];

							if (this.latAttribute && this.lngAttribute && !isNaN(parseInt(loc[this.latAttribute])) && !isNaN(parseInt(loc[this.lngAttribute]))) {
								return {
									lat: +loc[this.latAttribute],
									lon: +loc[this.lngAttribute],
									title,
									'Sys-Name': loc['Sys-Name'],
									'Sys-ObjectId': loc['Sys-ObjectId'],
									'Sys-Entity': loc['Sys-Entity'],
								};
							}

							if (loc[this.addressAttribute]) {
								const addressUpper = loc[this.addressAttribute].toUpperCase();
								const wasAddressInCache = !(typeof this.addressCache[addressUpper] === 'undefined');
								const point = await this.searchAddress(loc[this.addressAttribute]);
								if (!addressesToBeSavedIntoServerCache.includes(addressUpper) && !wasAddressInCache) {
									addressesToBeSavedIntoServerCache.push(addressUpper);
								}
								return point
									? {
											lat: +point.lat,
											lon: +point.lon,
											title,
											'Sys-Name': loc['Sys-Name'],
											'Sys-ObjectId': loc['Sys-ObjectId'],
											'Sys-Entity': loc['Sys-Entity'],
										}
									: null;
							}

							return null;
						})
					);

					// filter out rejected promises and nulls
					const successPoints = points
						.filter((p) => p.status === 'fulfilled')
						.map((p) => (p as PromiseFulfilledResult<Point>).value)
						.filter((p) => p !== null);

					finalSuccessPointsToDraw.push(...successPoints);

					await this.drawPoints(finalSuccessPointsToDraw);

					if (addressesToBeSavedIntoServerCache.length >= 10 || (addressesToBeSavedIntoServerCache.length > 0 && i + chunkSize >= sourceData.length)) {
						this.saveNewAddressesToServerCache(addressesToBeSavedIntoServerCache);
						addressesToBeSavedIntoServerCache = [];
					}
				}
			} catch (error) {
				console.error(error);
			}
			this.isLoading = false;
		}
	}

	async loadScripts() {
		if (this.leaflet) {
			return;
		}
		import('leaflet/dist/leaflet.css');
		this.leaflet = await import('leaflet');
		import('leaflet.markercluster/dist/MarkerCluster.css');
		import('leaflet.markercluster/dist/MarkerCluster.Default.css');
		if (!this.leaflet.MarkerClusterGroup) {
			// @ts-expect-error no type available for MarkerClusterGroup
			this.leaflet.MarkerClusterGroup = (await import('leaflet.markercluster')).MarkerClusterGroup;
		}

		this.leaflet.Icon.Default.imagePath = 'assets/modern_leaflet/';
	}

	async initMap(coords: [number, number], zoom?: number) {
		await this.loadingScriptsPromise;

		if (this.leaflet && !this.leafletMap) {
			this.leafletMap = this.leaflet.map(this.mxAtId).setView(coords, zoom ?? this.zoom ?? this.defaultZoom);
			this.leaflet
				?.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
					attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
				})
				.addTo(this.leafletMap);
		}
	}

	async loadAddressesFromServerCache(addresses: string[]): Promise<string[]> {
		try {
			const uniqueAddresses: string[] = addresses.map((a) => a.toUpperCase()).filter((address: string, index: number, self: string[]) => self.indexOf(address) === index);
			const coordsFromServerCache = ((await this.http.post(
				'api/map/coordinates/get',
				uniqueAddresses.map((address) => encodeURI(address))
			)) || []) as { Key: string; Value: string }[];

			coordsFromServerCache.forEach(({ Key, Value }) => {
				const coordsArr = JSON.parse(Value);
				if ((Array.isArray(coordsArr) && typeof coordsArr[0] === 'number' && typeof coordsArr[1] === 'number') || coordsArr === null) {
					this.addressCache[decodeURI(Key).toUpperCase()] = coordsArr as [number, number] | null;
				}
			});
			this.saveAddressesToLocalCache();
			return coordsFromServerCache.map(({ Key }) => decodeURI(Key).toUpperCase());
		} catch (error) {
			console.error('Getting coordinates from Address Server Cache failed');
			return [];
		}
	}

	async saveNewAddressesToServerCache(addresses: string[]): Promise<void> {
		try {
			const coordsToSave: AnyObject = {};
			addresses.forEach(async (address: string) => {
				const addressUpper = address.toUpperCase();
				const coords = this.addressCache[addressUpper];
				if ((Array.isArray(coords) && typeof coords[0] === 'number' && typeof coords[1] === 'number') || coords === null) {
					coordsToSave[encodeURI(addressUpper)] = JSON.stringify(coords);
				}
			});
			if (Object.keys(coordsToSave).length) {
				await this.http.post('api/map/coordinates', coordsToSave);
			}
		} catch (error) {
			console.error('Saving coordinates to Address Server Cache failed');
		}
	}

	saveAddressesToLocalCache(): void {
		const objectToSave: Record<string, [number, number] | null> = {};
		Object.keys(this.addressCache).forEach((address) => {
			const coordsArr = this.addressCache[address];
			if ((Array.isArray(coordsArr) && typeof coordsArr[0] === 'number' && typeof coordsArr[1] === 'number') || coordsArr === null) {
				objectToSave[address.toUpperCase()] = coordsArr;
			}
		});

		const jsonToSave = JSON.stringify(objectToSave);
		// save if less than 1MB
		if (jsonToSave.length < 1048576) {
			try {
				localStorage.setItem('mxGeoMapCache', jsonToSave);
			} catch (error) {
				console.error('Saving addresses to localStorage failed');
			}
		} else {
			localStorage.removeItem('mxGeoMapCache');
		}
	}

	async searchAddress(originAddress: string): Promise<Point | null> {
		const addressUpper = originAddress.toUpperCase();
		if (Array.isArray(this.addressCache[addressUpper])) {
			return { lat: this.addressCache[addressUpper][0], lon: this.addressCache[addressUpper][1] };
		} else if (this.addressCache[addressUpper] === null) {
			return null;
		} else if (this.addressCache[addressUpper] instanceof Promise) {
			return this.addressCache[addressUpper];
		} else {
			return (this.addressCache[addressUpper] = new Promise(async (resolve, reject) => {
				let point: Point | null | undefined = undefined; // null if not found, undefined if not yet searched

				// try to get coordinates from `https://photon.komoot.io/api`
				if (!point) {
					type photonPoint = { properties?: { country?: string; city?: string }; geometry?: { coordinates: [number, number] } };
					const getPhotonAddressPoints = async (address: string) => (await (await fetch('https://photon.komoot.io/api/?q=' + address)).json())?.features || [];
					try {
						const sourcePoints = await getPhotonAddressPoints(originAddress);
						let sourcePoint;
						if (sourcePoints.length) {
							// try to find the best match accourding to our inner format: <country>,<city>,<local-address>
							const addressParts = originAddress.split(',');
							if (addressParts.length > 2) {
								const country = addressParts[0].toUpperCase();
								const city = addressParts[1].toUpperCase();
								const find1stPointWithCountryAndCity = (points: photonPoint[]) =>
									points.find((p) => p?.properties?.country?.toUpperCase() === country && p?.properties?.city?.toUpperCase() === city);
								sourcePoint = find1stPointWithCountryAndCity(sourcePoints);
								// if such address is not found then show at least the City
								sourcePoint ||= find1stPointWithCountryAndCity(await getPhotonAddressPoints(`${country},${city}`));
							} else {
								// if country and city are not provided width expected format then just take the first point
								sourcePoint = sourcePoints[0];
							}
						}
						const coords = sourcePoint?.geometry?.coordinates;
						point = coords ? { lat: coords[1], lon: coords[0] } : null;
					} catch (error) {
						console.error('Error fetching address via `photon.komoot.io`');
					}
				}

				// try to get coordinates from `https://nominatim.openstreetmap.org`
				if (!point) {
					try {
						const response = await this.$http.get('https://nominatim.openstreetmap.org/search', {
							params: {
								format: 'json',
								q: originAddress,
							},
							cache: true,
						});
						const data = (response?.data as Point[]) ?? [];
						point = (data.length > 0 && data[0]) || null;
					} catch (error) {
						console.error('Error fetching address via `nominatim.openstreetmap.org`');
					}
				}

				if (typeof point !== 'undefined') {
					this.addressCache[addressUpper] = point ? [+point.lat, +point.lon] : null;
					this.saveAddressesToLocalCache();
					resolve(point);
				} else {
					delete this.addressCache[addressUpper];
					reject(new Error('No coordinates found for address: ' + originAddress));
				}
			}));
		}
	}

	private getWidgetInstanceTemplate(point: Point) {
		return `<mx-quick-link
					data-object-id="${point['Sys-ObjectId']}"
					data-entity-type="${point['Sys-Entity']}"
					data-title="${utils.htmlEncode(point['Sys-Name'])}"
					data-mode="link"
				></mx-quick-link>`;
	}

	private handlePopupOpen(e: Leaflet.PopupEvent) {
		const popupEl = e.popup.getElement();
		if (popupEl) {
			const compiled = this.$compile(popupEl.querySelector('.leaflet-popup-content') || '')(this.$rootScope.$new());
			$(popupEl).find('.leaflet-popup-content').replaceWith(compiled);
		}
	}

	async drawPoints(points: Point[], commonTitle: string = '') {
		// first clear all markers
		if (this.singleMarker) {
			this.singleMarker.remove();
		}
		if (this.markerGroup) {
			this.markerGroup.clearLayers();
		}

		const resPoints = points.filter((p) => {
			return typeof p.lat === 'number' && typeof p.lon === 'number';
		});

		if (resPoints.length) {
			if (!this.leafletMap) {
				await this.initMap([+resPoints[0].lat, +resPoints[0].lon]);
			}

			const getPopupContent = (p: Point): string => {
				let popupContent = p.title || commonTitle;

				if (p['Sys-Name'] && p['Sys-Entity'] && p['Sys-ObjectId']) {
					if (popupContent?.includes(p['Sys-Name'])) {
						popupContent = popupContent.replace(p['Sys-Name'], this.getWidgetInstanceTemplate(p));
					} else {
						popupContent = this.getWidgetInstanceTemplate(p) + ` - ` + popupContent;
					}
				}
				return popupContent;
			};

			if (resPoints.length === 1 && this.leafletMap) {
				this.singleMarker = this.leaflet?.marker([resPoints[0].lat, resPoints[0].lon]).bindPopup(getPopupContent(resPoints[0])).addTo(this.leafletMap);
				this.singleMarker?.on('popupopen', (e) => this.handlePopupOpen(e));
				this.singleMarker?.openPopup();
			} else if (resPoints.length > 1 && this.leaflet && this.leafletMap) {
				this.markerGroup = new this.leaflet.MarkerClusterGroup();
				resPoints.forEach((p) => {
					const marker = this.leaflet?.marker([+p.lat, +p.lon], { title: p.title || commonTitle });
					if (marker) {
						marker.bindPopup(getPopupContent(p));
						marker.openPopup();
						marker.on('popupopen', (e) => this.handlePopupOpen(e));
						this.markerGroup?.addLayer(marker);
					}
				});
				this.leafletMap?.addLayer(this.markerGroup);
			}
			// center map to see all dots
			if (resPoints.length > 1 && !this.zoom && this.zoom !== 0) {
				if (this.centeringMapTimeout) {
					clearTimeout(this.centeringMapTimeout);
				}
				this.centeringMapTimeout = setTimeout(() => {
					const bounds = this.leaflet?.latLngBounds(resPoints.map((p) => [p.lat, p.lon]));
					this.leafletMap?.fitBounds(bounds!);
				}, 1000);
			} else if (resPoints.length === 1) {
				// ...or center to the first point
				this.leafletMap?.setView([resPoints[0].lat, resPoints[0].lon], typeof this.zoom === 'number' ? this.zoom : this.defaultZoom);
			}
		}
	}

	handleContainerResizing() {
		const resizeObserver = new ResizeObserver(
			$.debounce(300, () => {
				window.dispatchEvent(new Event('resize'));
			})
		);
		resizeObserver.observe(this.$element[0]);
	}
}

export const GeoMapComponent = {
	name: 'mxGeoMap',
	controller: GeoMapController,
	controllerAs: 'vm',
	template,
	bindings: {
		zoom: '<',
		sourceData: '<',
		addressAttribute: '@',
		latAttribute: '@',
		lngAttribute: '@',
		titleAttribute: '@',
	},
};
